import { AxiosInstance } from 'axios';
import createAuthRefreshInterceptor from 'refresh';

import type { AxiosAuthRefreshCallback } from 'v2/types';
import type { IAuthConfig, IAuthProvider } from 'v2/providers/types';
import { AuthenticationError } from 'shared/errors';
import { runInsideLock } from 'v2/concurrent';
import { safeLs } from 'shared/storages';

import { authStorage } from './storages';
import { isAuthCompletePage } from './helpers';
import {
  checkCurrentAuth,
  checkQueryAuthParams,
  completeAuthorization,
  goToAuth,
} from './actions';
import { createResponseInterceptor, requestInterceptor } from './interceptors';
import type { ISamoletConfiguration, ISamoletProviderOptions } from './types';
import { SamoletOAuthURLResolver } from './urls';
import { checkSso } from './api';

export class SamoletAuthProvider implements IAuthProvider {
  private readonly _config: ISamoletConfiguration;

  private readonly _responseInterceptor: AxiosAuthRefreshCallback;

  constructor(config: IAuthConfig, options?: ISamoletProviderOptions) {
    options = options ?? {};

    this._config = {
      clientId: config.clientId,
      allowAnonymous: !!config.allowAnonymous,
      urls: new SamoletOAuthURLResolver(options.useStagingEnvironment),
    };

    this._responseInterceptor = createResponseInterceptor(this._config);
  }

  async init() {
    await safeLs.add('SAMOLET_OAUTH2_PKCE_CLIENT_ID', this._config.clientId);

    return await runInsideLock(async () => {
      if (isAuthCompletePage()) {
        await completeAuthorization(this._config);

        return false;
      }

      try {
        await checkQueryAuthParams();

        const authenticated = await checkCurrentAuth(this._config);
        if (!authenticated) {
          if (this._config.allowAnonymous && !(await checkSso(this._config))) {
            return false;
          }

          await goToAuth(this._config);
        }

        return authenticated;
      } catch (error: unknown) {
        const message = 'Library init error';
        console.log(message, error);
        throw new AuthenticationError(message);
      }
    });
  }

  login() {
    return goToAuth(this._config);
  }

  async logout(redirectUrl?: string) {
    await authStorage.cleanJwtAuthParams();
    window.location.href = this._config.urls.getLogoutUrl(redirectUrl);
  }

  getLogoutUrl(redirectUrl?: string): string {
    return this._config.urls.getLogoutUrl(redirectUrl);
  }

  getToken(): string | undefined {
    return authStorage.getJwtAuthParams()?.access;
  }

  setAuthInterceptor(instance: AxiosInstance) {
    instance.interceptors.request.use(requestInterceptor);
    createAuthRefreshInterceptor(instance, this._responseInterceptor, {
      statusCodes: [401],
    });
  }
}
